import { useIntl } from "react-intl";
import { appendInitialReferrer } from "src/exits/url/appendInitialReferrer";
import type { SearchResponse } from "src/api/SearchResponse";
import { logDataPartnerCarHireExit } from "src/data-partners/logDataPartnerExit";
import { buildCarHireRedirectUrl } from "src/domain/SegmentScreen/createSegmentScreenViewModel";
import KeySolid from "src/design-system/icons/KeySolid.svg?react";
import { originPlaceFromSegment } from "src/utils/adapters/place";
import { localeToLanguageCode } from "src/utils/conversions/languageCode";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import useSearch from "src/utils/hooks/useSearch";
import type { Mode } from "src/utils/types/mode";
import { useExitLink } from "src/exits/hooks/useExitLink";
import { SegmentPromoDriveVariant } from "../SegmentPromoDriveVariant/SegmentPromoDriveVariant";
import messages from "./SegmentCarHirePromoDriveVariant.messages";

type Props = {
  location: string;
  segmentIndex: number;
  searchResponse: SearchResponse;
  transitMode: Mode;
};

export function SegmentCarHirePromoDriveVariant({
  location,
  segmentIndex,
  searchResponse,
  transitMode,
}: Props) {
  const intl = useIntl();
  const { isMdScreen } = useScreenMinWidth();
  const isFullSizePromo = !isMdScreen;

  const { origin, destination } = useSearch();
  const originPlace = originPlaceFromSegment(searchResponse, segmentIndex);
  let carHirePromoUrl = buildCarHireRedirectUrl(
    originPlace,
    transitMode,
    searchResponse,
  );
  const [_, handleExit] = useExitLink({
    href: carHirePromoUrl ? appendInitialReferrer(carHirePromoUrl) : "",
    kind: "RentalCars",
    exitPoint: {
      view: "segment",
      trigger: "carCta",
      transitMode,
    },
    onExit({ url, sendExitInteractionEvent }) {
      sendExitInteractionEvent({
        category: "Segment",
        action: "Click:CarHirePromo",
      });

      logDataPartnerCarHireExit({
        origin: origin?.canonicalName,
        destination: destination?.canonicalName,
        languageCode: localeToLanguageCode(intl.locale),
        isAdaraEnabled: searchResponse.analytics?.interest_data?.Adara?.enabled,
      });

      window.open(url, "_blank", "noopener noreferrer");
    },
  });

  return (
    <SegmentPromoDriveVariant
      heading={intl.formatMessage(messages.heading, { location })}
      tagline={intl.formatMessage(messages.tagline)}
      icon={<KeySolid className="text-icon-primary" />}
      iconAlign={isFullSizePromo ? "left" : "right"}
      outline={isFullSizePromo}
      fullWidth={isFullSizePromo}
      onClick={handleExit}
    />
  );
}
